import { useState } from 'react';
import { Box, Button, Chip, Collapse, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { LayoutOption } from '@/types';
import { formatPrice } from '@/utils/price.utils';
import { toTitleCase } from '@/utils/text.utils';

interface LayoutOptionCardProps {
  layout: LayoutOption;
  selected: boolean;
  onSelect: () => void;
  /** Etichetta del bottone di conferma (default "Conferma questa scelta"). */
  confirmLabel?: string;
}

const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  height: '100%',
  backgroundColor: theme.palette.background.paper,
  border: `${selected ? 2 : 1}px solid`,
  borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
}));

/** Card di una variante di planimetria (LAYOUT A/B/BP) nel primo step di LAYOUT. */
export function LayoutOptionCard({
  layout,
  selected,
  onSelect,
  confirmLabel = 'Conferma questa scelta',
}: LayoutOptionCardProps) {
  const [showQualities, setShowQualities] = useState(false);
  const [imgError, setImgError] = useState(false);
  // La planimetria è l'immagine composita del piano base (planComposeUrl); `fileLabelBaseUrl` è solo
  // una cartella, non renderizzabile. Placeholder finché il BE non espone planComposeUrl.
  const planimetryUrl = layout.planComposeUrl ?? null;

  return (
    <CardRoot selected={selected}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`Layout ${layout.code}`} color="primary" variant={selected ? 'filled' : 'outlined'} size="small" />
          {layout.isRecommended && <Chip label="Consigliato" color="secondary" size="small" />}
        </Box>
        {selected && <CheckCircleIcon color="primary" fontSize="small" aria-hidden="true" />}
      </Box>

      {/* Planimetria della variante (placeholder finché il BE non espone un'immagine reale). */}
      {planimetryUrl && !imgError ? (
        <Box
          component="img"
          src={planimetryUrl}
          alt={`Planimetria layout ${layout.code}`}
          loading="lazy"
          onError={() => setImgError(true)}
          sx={{ width: '100%', height: 200, objectFit: 'contain', bgcolor: '#f6f6f6', borderRadius: 1 }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f6f6f6',
            borderRadius: 1,
            color: 'text.disabled',
            fontSize: 13,
          }}
        >
          Planimetria non disponibile
        </Box>
      )}

      {layout.areaLivingCamere != null && layout.areaLivingCamere > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Superficie zona giorno e notte
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {layout.areaLivingCamere} m²
          </Typography>
        </Box>
      )}

      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        {layout.rooms.map((r) => (
          <Chip
            key={r.type}
            size="small"
            variant="outlined"
            label={r.count > 1 ? `${toTitleCase(r.type)} ×${r.count}` : toTitleCase(r.type)}
          />
        ))}
      </Stack>

      {layout.longDescription && (
        <Box>
          <Button
            variant="text"
            size="small"
            endIcon={<ExpandMoreIcon sx={{ transform: showQualities ? 'rotate(180deg)' : 'none', transition: '200ms' }} />}
            onClick={() => setShowQualities((v) => !v)}
            sx={{ pl: 0 }}
          >
            Scopri le qualità di questo layout
          </Button>
          <Collapse in={showQualities}>
            <Box
              sx={{ fontSize: 14, color: 'text.secondary', '& p': { m: 0, mb: 1 } }}
              // Rich text delle qualità fornito dal backend (nostra sorgente controllata).
              dangerouslySetInnerHTML={{ __html: layout.longDescription }}
            />
          </Collapse>
        </Box>
      )}

      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pt: 1 }}>
        <Typography variant="subtitle1" color="primary.main" sx={{ fontWeight: 700 }}>
          {layout.price > 0 ? `+ ${formatPrice(layout.price)}` : 'Incluso nel capitolato'}
        </Typography>
        <Button variant={selected ? 'contained' : 'outlined'} color="primary" onClick={onSelect}>
          {selected ? 'Selezionato' : confirmLabel}
        </Button>
      </Box>
    </CardRoot>
  );
}
